// immediately caught what that means on the ground: *"It doesn't map the sample points at the
// points themselves (and often isn't even close)"* — of course not; the actual samples were never
// an input to that surface, neither their locations nor their lab values.
import type {
  ManifestLayer,
  SampleEvent,
  SoilSamplePoint,
} from "../types/contract";
import { blobPropOf } from "./depthDisplay";
import {
  ebkKedDraws,
  fitVariogramRobust,
  fwdLog,
  invLog,
  krigeDual,
  KRIGING_BOUNDS,
  KRIGING_METHOD,
  KRIGING_SPACE,
  rfrkOk,
  simpleKrige,
  type Variogram,
} from "./krigeEngine";
import { isLayerVisible, type LayerUiState } from "./layerRegistry";
import {
  labelOf,
  OLD_SOIL_PREFIX,
  SOIL_LAYER_PREFIX,
  sampleBlobToM3,
} from "./soilLayers";
import { lensedValue } from "./mapValues";
import { METRES_PER_DEGREE_LAT, METRES_PER_DEGREE_LNG } from "./metres";

/* * The five modelled properties the comparison exists for. */
export const CHECKABLE_PROPS = new Set(["ph", "om", "cec", "p", "k"]);

export interface IdwPoint {
  lng: number;
  lat: number;
  /* * the stored Mehlich-3-at-8" value — the basis both surfaces and the hover share */
  value: number;
}

export interface CheckAccuracyTarget {
  /* * the visible High-Res modelled surface (a `ml-<prop>` COG from the manifest) */
  layer: ManifestLayer;
  /* * its property — 'ph' | 'om' | 'cec' | 'p' | 'k' */
  prop: string;
  /* * the located current-event samples the IDW pane is built from — snapshotted with the target */
  points: IdwPoint[];
}

/* * Fewer located samples than this and an interpolated "map" is a near-constant blur, not a map. */
export const MIN_SAMPLE_POINTS = 3;

/*
 *
 * Zone-attached results (`zones:` events): their stored location is `ST_PointOnSurface` bookkeeping
 * not a place anyone sampled — IDW from it would invent spatial precision a composite never had.
 * Samples with no GPS or no convertible value: nothing honest to anchor.
 */
export function idwSamplePoints(
  samples: SoilSamplePoint[] | null | undefined,
  events: SampleEvent[] | undefined,
  prop: string,
): IdwPoint[] {
  const currentId = events?.find((e) => e.is_current)?.event_id;
  const out: IdwPoint[] = [];
  for (const s of samples ?? []) {
    if (!Number.isFinite(s.lon) || !Number.isFinite(s.lat)) continue;
    if (s.sample_event_id?.startsWith("zones:")) continue;
    if (
      currentId != null &&
      s.sample_event_id != null &&
      s.sample_event_id !== currentId
    ) {
      const ev = events!.find((e) => e.event_id === s.sample_event_id);
      if (ev?.has_results && !ev.is_current) continue; // an OLD event's point
    }
    const v = sampleBlobToM3(s)?.[prop];
    if (typeof v === "number" && Number.isFinite(v))
      out.push({ lng: s.lon, lat: s.lat, value: v });
  }
  return out;
}

/*
 *
 * High-res means: a manifest `ml-*` row served as a signed COG, built by the ML ensemble.
 */
export function highResPropOf(layer: ManifestLayer): string | null {
  if (!layer.id?.startsWith("ml-") || layer.kind !== "cog" || !layer.signed_url)
    return null;
  // `method` is the router's discriminator (ml-* rows only, null = the ML lane); the title suffix
  // is the fallback for rows cached before the field was typed. genius_zones too: it is kriged from
  // user-placed, zone-corrected cores, so recreating it from the lab samples and calling the
  // difference "accuracy" would be comparing two different inputs.
  if (
    layer.method === "idw" ||
    layer.method === "zones_flat" ||
    layer.method === "genius_zones"
  )
    return null;
  if (/\((?:idw|zone readings|genius zones)\)/i.test(layer.title ?? ""))
    return null;
  if (layer.id === "ml-map") return "ph";
  const prop = blobPropOf(layer.id ?? layer.title);
  return prop != null && CHECKABLE_PROPS.has(prop) ? prop : null;
}

/*
 *
 * What the Check Accuracy button offers right now, or null (no button).
 *
 * Offered only when BOTH halves of the comparison exist: a visible high-res layer with a usable
 * colour range, AND enough located current-event samples to interpolate — a button that opens onto an
 * error (or onto a near-constant "map" of two points) is worse than no button. Radio-solo visibility
 * means at most one data layer is visible; `find` takes the first for the degenerate case.
 */
export function checkAccuracyTargetOf(
  layers: ManifestLayer[] | null | undefined,
  ui: Record<string, LayerUiState>,
  samples: SoilSamplePoint[] | null | undefined,
  events: SampleEvent[] | undefined,
): CheckAccuracyTarget | null {
  for (const layer of layers ?? []) {
    const prop = highResPropOf(layer);
    if (prop == null || !isLayerVisible(layer, ui)) continue;
    // no usable colour range = no honest side-by-side: `alignedIdwLayer` could not put the panes on
    // one scale, so the view would silently break its own "same value, same colour" promise.
    if (
      layer.value_min == null ||
      layer.value_max == null ||
      !(layer.value_max > layer.value_min)
    )
      continue;
    const points = idwSamplePoints(samples, events, prop);
    if (points.length >= MIN_SAMPLE_POINTS) return { layer, prop, points };
  }
  return null;
}

export const PROP_ORDER = ["p", "k", "om", "ph", "cec"] as const;
export const PROP_LABEL: Record<string, string> = {
  p: "P",
  k: "K",
  om: "OM",
  ph: "pH",
  cec: "CEC",
};

/*
 *
 * Same gates as `checkAccuracyTargetOf` EXCEPT visibility: the dropdown's whole point is reaching
 * the maps that are NOT the viewed layer.
 */
export function checkAccuracyTargetsFor(
  layers: ManifestLayer[] | null | undefined,
  samples: SoilSamplePoint[] | null | undefined,
  events: SampleEvent[] | undefined,
): CheckAccuracyTarget[] {
  const out: CheckAccuracyTarget[] = [];
  for (const wanted of PROP_ORDER) {
    for (const layer of layers ?? []) {
      if (highResPropOf(layer) !== wanted) continue;
      if (
        layer.value_min == null ||
        layer.value_max == null ||
        !(layer.value_max > layer.value_min)
      )
        continue;
      const points = idwSamplePoints(samples, events, wanted);
      if (points.length >= MIN_SAMPLE_POINTS)
        out.push({ layer, prop: wanted, points });
      break; // first accepted layer per property (radio-solo main map has at most one anyway)
    }
  }
  return out;
}

// compute's Classic-Grid kernel (grid_om_idw.py) — the same constants, so this pane answers the same
// question that school answers, just from the REAL samples instead of a synthetic grid
export const IDW_POWER = 3;
export const IDW_K = 12;

/*
 *
 * Distances are metric (equirectangular, cos-lat corrected — the same approximation every
 * design/area computation in the app uses at field scale); raw-degree distances would stretch
 * north-south by ~30% at latitudes and quietly bias every weight.
 */
export function idwSurface(
  points: IdwPoint[],
  centroids: { lng: number; lat: number }[],
  power = IDW_POWER,
  k = IDW_K,
): (number | null)[] {
  if (!points.length) return centroids.map(() => null);
  const lat0 = points.reduce((a, p) => a + p.lat, 0) / points.length;
  const kx = METRES_PER_DEGREE_LNG * Math.cos((lat0 * Math.PI) / 180);
  const ky = METRES_PER_DEGREE_LAT;
  const kEff = Math.min(k, points.length);
  return centroids.map((c) => {
    const d = points.map((p) =>
      Math.hypot((p.lng - c.lng) * kx, (p.lat - c.lat) * ky),
    );
    // k nearest: sample counts are tens, cells are thousands — a sort per cell is cheap and clear
    const order = d
      .map((_dist, i) => i)
      .sort((a, b) => d[a]! - d[b]!)
      .slice(0, kEff);
    let wSum = 0;
    let vSum = 0;
    for (const i of order) {
      const w = 1 / Math.max(d[i]!, 1e-3) ** power;
      wSum += w;
      vSum += w * points[i]!.value;
    }
    return vSum / wSum;
  });
}

/*
 *
 * The DISPLAY variogram for kriging raw sample values — after the flat-pane report sill = the
 * sample variance, nugget PINNED at 5% of it, effective range = the MEDIAN PAIRWISE distance
 * (practical-range convention: cov decays ~95% there, so lenScale = range/3) — exactly the
 * `lib/kriging.
 */
export function displayVariogram(
  sx: Float64Array,
  sy: Float64Array,
  val: Float64Array,
): Variogram {
  const n = val.length;
  let m = 0;
  for (let i = 0; i < n; i++) m += val[i]! / n;
  let v = 0;
  for (let i = 0; i < n; i++) v += (val[i]! - m) ** 2 / n;
  const floor = Math.max(1e-9 * (1 + m * m), 1e-12);
  const dists: number[] = [];
  for (let i = 0; i < n; i++)
    for (let j = i + 1; j < n; j++) {
      dists.push(Math.sqrt((sx[i]! - sx[j]!) ** 2 + (sy[i]! - sy[j]!) ** 2));
    }
  dists.sort((a, b) => a - b);
  const range = Math.max(1, dists[Math.floor(dists.length / 2)] ?? 1);
  return {
    sill: Math.max(0.95 * v, floor),
    lenScale: range / 3,
    nugget: Math.max(0.05 * v, floor * 0.05),
  };
}

/*
 *
 * IDW is the DEGENERATE fallback only — a singular solve that survives the jitter retry (in
 * practice: never) — and the caller labels it, mirroring compute's own `ordinary_kriging` which
 * falls back to IDW below 3 points rather than refusing to map.
 */
export function sampleKrigeSurface(
  points: IdwPoint[],
  centroids: { lng: number; lat: number }[],
): { values: (number | null)[]; usedIdwFallback: boolean } {
  if (!points.length)
    return { values: centroids.map(() => null), usedIdwFallback: false };
  const lat0 = points.reduce((a, p) => a + p.lat, 0) / points.length;
  const kx = METRES_PER_DEGREE_LNG * Math.cos((lat0 * Math.PI) / 180);
  const ky = METRES_PER_DEGREE_LAT;
  const sx = Float64Array.from(points, (p) => p.lng * kx);
  const sy = Float64Array.from(points, (p) => p.lat * ky);
  const val = Float64Array.from(points, (p) => p.value);
  const gx = Float64Array.from(centroids, (c) => c.lng * kx);
  const gy = Float64Array.from(centroids, (c) => c.lat * ky);
  const mean = points.reduce((a, p) => a + p.value, 0) / points.length;
  const vm = displayVariogram(sx, sy, val);
  const kriged = simpleKrige(sx, sy, val, vm, mean, gx, gy);
  if (kriged) return { values: Array.from(kriged), usedIdwFallback: false };
  return { values: idwSurface(points, centroids), usedIdwFallback: true };
}

export function sampleKrigeLayer(
  points: IdwPoint[],
  geoms: { type?: string; coordinates?: unknown }[],
  centroids: { lng: number; lat: number }[],
  hr: ManifestLayer,
  prop: string,
  /* * already-kriged values (the holdout scoreboard reads these SAME numbers) — computed here when absent */
  precomputed?: (number | null)[],
): ManifestLayer {
  const values = precomputed ?? sampleKrigeSurface(points, centroids).values;
  const features = geoms.reduce<unknown[]>((acc, g, i) => {
    const v = values[i];
    // value_stored = value: the underlay is already on the stored M3-8″ basis the dots colour from.
    // Without the twin, paint coalesced a missing property to 0/NaN and the whole field went one colour.
    if (v != null && Number.isFinite(v))
      acc.push({
        type: "Feature",
        properties: { value: v, value_stored: v },
        geometry: g,
      });
    return acc;
  }, []);
  const present = values.filter(
    (v): v is number => v != null && Number.isFinite(v),
  );
  const vmin = present.length ? Math.min(...present) : 0;
  const vmax = present.length ? Math.max(...present) : 1;
  const label: Record<string, string> = {
    ph: "pH",
    om: "OM",
    cec: "CEC",
    p: "P",
    k: "K",
  };
  const layer: ManifestLayer = {
    id: `check-sample-krige-${prop}`,
    kind: "geojson",
    title: `${label[prop] ?? prop} (sample kriging)`,
    inline: { type: "FeatureCollection", features } as never,
    colormap: "viridis",
    value_min: vmin,
    value_max: vmax > vmin ? vmax : vmin + 1,
    unit: hr.unit,
    default_visible: true,
    default_opacity: hr.default_opacity,
    order: 55,
    content_version: "check-accuracy",
    is_stale: false,
  } as ManifestLayer;
  return alignedIdwLayer(layer, hr);
}

/*
 *
 * The kriging points of a sample LAYER — read from its own features, so a surface kriged from them
 * shows exactly what the dots say (the one-source rule the Check Accuracy panes follow). Only
 * Point-geometry features count: zone-attached (areal) readings sit at bookkeeping locations nobody
 * sampled, and kriging those would invent spatial structure from polygon centroids.
 */
export function sampleLayerPoints(
  layer: ManifestLayer,
  opts?: { basis?: "display" | "stored" },
): IdwPoint[] {
  const fc = layer.inline as GeoJSON.FeatureCollection | undefined;
  return (fc?.features ?? []).flatMap((f) => {
    if (f.geometry?.type !== "Point") return [];
    // a REAL number only — Number(null) is 0, and a missing reading kriged as zero would dent the
    // whole neighbourhood of the surface around it.
    const p = f.properties as Record<string, unknown> | null;
    // 'stored' = the M3-8″ twin baked beside the display value: analysis runs on the stored basis
    // even when the dots follow the depth picker; falls back to `value` for layers built before the
    // twin existed.
    const v =
      opts?.basis === "stored" ? (p?.value_stored ?? p?.value) : p?.value;
    const [lng, lat] = (f.geometry as GeoJSON.Point).coordinates;
    return typeof v === "number" &&
      Number.isFinite(v) &&
      Number.isFinite(lng) &&
      Number.isFinite(lat)
      ? [{ lng: lng!, lat: lat!, value: v }]
      : [];
  });
}

/*
 *
 * Old-event layers are excluded: the model was built from the CURRENT event, so old points would
 * grade it against answers it never saw.
 */
export function samplesLayerFor(
  layers: ManifestLayer[] | null | undefined,
  prop: string,
): ManifestLayer | null {
  const base = `${SOIL_LAYER_PREFIX}${prop}`;
  const eligible = (layers ?? []).filter(
    (l) => !l.id.startsWith(OLD_SOIL_PREFIX),
  );
  return (
    eligible.find((l) => l.id === `${base}-8in`) ??
    eligible.find((l) => l.id === base) ??
    null
  );
}

/*
 *
 * The whole point of the split is comparing the two surfaces BY EYE, and the comparison is only
 * honest if the same value paints the same colour on both sides.
 */
/*
 *
 * Re-base a display-lensed samples layer onto STORED M3-8″: Check Accuracy's panes are stored-basis
 * surfaces and its kriged pane is an exact interpolator at the dots, so an at-depth-baked dot would
 * disagree with the very ground it sits on — manufacturing the depth “disagreement” the view exists
 * to rule out.
 */
export function rebaseToStored(layer: ManifestLayer): ManifestLayer {
  if (layer.atDisplayDepth !== true) return layer;
  const fc = layer.inline as GeoJSON.FeatureCollection | undefined;
  if (!fc) return layer;
  const features = fc.features.map((f) => {
    const p = (f.properties ?? {}) as Record<string, unknown>;
    const s = p.value_stored;
    if (typeof s !== "number" || !Number.isFinite(s)) return f;
    return {
      ...f,
      properties: {
        ...p,
        value: s,
        label: labelOf(lensedValue(layer, s, false).value),
      },
    };
  });
  const vals = features
    .map((f) => (f.properties as { value?: unknown }).value)
    .filter((v): v is number => typeof v === "number" && Number.isFinite(v));
  return {
    ...layer,
    inline: { ...fc, features } as never,
    atDisplayDepth: false,
    title: (layer.title ?? "").replace(/ · at \d+″/, ""),
    value_min: vals.length ? Math.min(...vals) : layer.value_min,
    value_max: vals.length ? Math.max(...vals) : layer.value_max,
  };
}

export function alignedIdwLayer(
  idw: ManifestLayer,
  hr: ManifestLayer,
): ManifestLayer {
  if (
    hr.value_min == null ||
    hr.value_max == null ||
    !(hr.value_max > hr.value_min)
  )
    return idw;
  return { ...idw, value_min: hr.value_min, value_max: hr.value_max };
}

// THE LEFT PANE: the High-Res map RECREATED LIVE — "the map that appears in th left panel to be
// essentially a recreation of the high-res map using the uncorrected layer and the process we've
// discussed").

/* * the uncorrected GBDT-ensemble trend column per property — the SAME keys accuracyIndex scores against */
export const UNCORRECTED_KEY: Record<string, string> = {
  p: "model__uncorrected_p",
  k: "model__uncorrected_k",
  ph: "model__uncorrected_ph",
  om: "model__uncorrected_om",
  cec: "model__uncorrected_cec",
};

/*
 * Below this the variogram fit is guesswork and the recreation falls back to the SAVED surface —
 * compute itself refuses a property under a handful of samples.
 */
export const MIN_KRIGE_POINTS = 8;

/*
 * shared metric projection (equirectangular, cos-lat) — one projector for samples AND cells, or the
 * kriging distances between them mean nothing
 */
function toMetric(
  points: IdwPoint[],
  centroids: { lng: number; lat: number }[],
): {
  sx: Float64Array;
  sy: Float64Array;
  gx: Float64Array;
  gy: Float64Array;
} {
  const lat0 = points.reduce((a, p) => a + p.lat, 0) / points.length;
  const kx = METRES_PER_DEGREE_LNG * Math.cos((lat0 * Math.PI) / 180);
  const ky = METRES_PER_DEGREE_LAT;
  const sx = new Float64Array(points.length);
  const sy = new Float64Array(points.length);
  points.forEach((p, i) => {
    sx[i] = p.lng * kx;
    sy[i] = p.lat * ky;
  });
  const gx = new Float64Array(centroids.length);
  const gy = new Float64Array(centroids.length);
  centroids.forEach((c, i) => {
    gx[i] = c.lng * kx;
    gy[i] = c.lat * ky;
  });
  return { sx, sy, gx, gy };
}

export interface RecreateResult {
  /* * per-cell corrected values, index-aligned with the centroids; null where the trend is absent */
  values: (number | null)[];
  /*
   *
   * Null when no OK twin solved (in practice never while `values` exists).
   */
  valuesOk: (number | null)[] | null;
  /* * which estimator actually ran — 'ebk' can degrade to 'rfrk_ok' exactly as infer.py does */
  method: "ebk" | "rfrk_ok";
  /* * true when EBK was the plan but every KED draw was singular */
  usedFallback: boolean;
}

/* * The model-influence blend: null-preserving per-cell lerp between the μ=0 and μ=1 surfaces. */
export function lerpSurfaces(
  ok: (number | null)[],
  ek: (number | null)[],
  mu: number,
): (number | null)[] {
  return ek.map((e, i) => {
    const o = ok[i];
    if (e == null || o == null) return null;
    return o + (e - o) * mu;
  });
}

/*
 *
 * The High-Res correction, recreated: uncorrected trend + residual interpolation from the CURRENT
 * samples, per property. infer.py's exact recipe: pH/P/CEC/OM → bagged EBK-KED (log1p space for
 * P/CEC/OM, native pH), K → RFRK_OK; every-draw-singular → RFRK_OK native; physical-bounds clamp
 * trend AT a sample = its nearest finite cell (production's own home-cell read); non-finite-trend
 * cells stay unmapped.
 *
 * A GENERATOR yielding after each EBK bag (progress 0..1), so the caller can hand the event loop back
 * between bags — the return value arrives via the iterator's `done` step. Deliberate divergences from
 * the server (bag RNG, variogram search, no old-event pooling) are documented on krigeEngine.
 */
export function* recreateHighRes(
  points: IdwPoint[],
  centroids: { lng: number; lat: number }[],
  trend: (number | null | undefined)[],
  prop: string,
): Generator<number, RecreateResult | null> {
  if (points.length < MIN_KRIGE_POINTS || centroids.length === 0) return null;
  const { sx, sy, gx, gy } = toMetric(points, centroids);
  const gtrend = new Float64Array(centroids.length);
  for (let c = 0; c < centroids.length; c++) {
    const t = trend[c];
    gtrend[c] = typeof t === "number" && Number.isFinite(t) ? t : Number.NaN;
  }
  // Samples farther than 30 m from a finite cell are dropped. Re-homing them with no
  // cap would reshape the variogram and the kriging weights in the comparison that
  // judges the served map.
  const MAX_HOME_M = 30;
  const keptObs: number[] = [];
  const keptDrift: number[] = [];
  const keptSx: number[] = [];
  const keptSy: number[] = [];
  for (let i = 0; i < points.length; i++) {
    let home = -1;
    let bestD = Infinity;
    for (let c = 0; c < centroids.length; c++) {
      const d = (gx[c]! - sx[i]!) ** 2 + (gy[c]! - sy[i]!) ** 2;
      if (d < bestD) {
        bestD = d;
        home = c;
      }
    }
    if (home < 0 || bestD > MAX_HOME_M * MAX_HOME_M) continue;
    if (!Number.isFinite(gtrend[home]!)) continue;
    keptObs.push(points[i]!.value);
    keptDrift.push(gtrend[home]!);
    keptSx.push(sx[i]!);
    keptSy.push(sy[i]!);
  }
  // the drops can take the set below the floor — the caller's saved-surface fallback, not an error
  if (keptObs.length < MIN_KRIGE_POINTS) return null;
  const obs = Float64Array.from(keptObs);
  const drift = Float64Array.from(keptDrift);
  const ksx = Float64Array.from(keptSx);
  const ksy = Float64Array.from(keptSy);

  let method: "ebk" | "rfrk_ok" = KRIGING_METHOD[prop] ?? "rfrk_ok";
  let usedFallback = false;
  let pred: Float64Array | null = null;
  let predOk: Float64Array | null = null;

  if (method === "ebk") {
    const space = KRIGING_SPACE[prop] ?? "native";
    const fwd = space === "log" ? fwdLog : (v: number) => v;
    const inv = space === "log" ? invLog : (v: number) => v;
    // the transform applies to observations AND drift, at samples AND cells — infer.py's _fwd_inv
    const obsT = Float64Array.from(obs, fwd);
    const driftT = Float64Array.from(drift, fwd);
    // KED runs over FINITE-trend cells only (grid_ok); NaN cells never enter the system
    const okIdx: number[] = [];
    for (let c = 0; c < centroids.length; c++)
      if (Number.isFinite(gtrend[c]!)) okIdx.push(c);
    const kgx = Float64Array.from(okIdx, (c) => gx[c]!);
    const kgy = Float64Array.from(okIdx, (c) => gy[c]!);
    const kgd = Float64Array.from(okIdx, (c) => fwd(gtrend[c]!));
    const acc = new Float64Array(okIdx.length);
    const okAcc = new Float64Array(okIdx.length);
    let cnt = 0;
    let okCnt = 0;
    let bag = 0;
    const N_BAG = 20;
    for (const draw of ebkKedDraws(ksx, ksy, obsT, driftT, kgx, kgy, kgd, {
      nBag: N_BAG,
    })) {
      bag++;
      if (draw.ked) {
        for (let c = 0; c < draw.ked.length; c++) acc[c] += draw.ked[c]!;
        cnt++;
      }
      if (draw.ok) {
        for (let c = 0; c < draw.ok.length; c++) okAcc[c] += draw.ok[c]!;
        okCnt++;
      }
      yield bag / N_BAG;
    }
    if (cnt > 0) {
      pred = new Float64Array(centroids.length).fill(Number.NaN);
      for (let k = 0; k < okIdx.length; k++)
        pred[okIdx[k]!] = inv(acc[k]! / cnt);
      if (okCnt > 0) {
        predOk = new Float64Array(centroids.length).fill(Number.NaN);
        for (let k = 0; k < okIdx.length; k++)
          predOk[okIdx[k]!] = inv(okAcc[k]! / okCnt);
      }
    } else {
      // infer.py: "If every KED draw is singular … fall back to RFRK_OK so the property still maps"
      method = "rfrk_ok";
      usedFallback = true;
    }
  }
  if (method === "rfrk_ok") {
    // hand the event loop back before the solve — the K path yields nothing otherwise, and the
    // EBK-singular fallback otherwise ran its whole solve AFTER the progress line said 100%
    yield 0;
    const resid = Float64Array.from(obs, (v, i) => v - drift[i]!);
    pred = rfrkOk(ksx, ksy, resid, gx, gy, gtrend);
    // the μ=0 twin: OK of the RAW values on the same masked grid.
    const vmResid = fitVariogramRobust(ksx, ksy, resid);
    const okY = krigeDual(ksx, ksy, obs, vmResid, null, gx, gy, null);
    if (okY) {
      predOk = new Float64Array(centroids.length).fill(Number.NaN);
      for (let c = 0; c < centroids.length; c++)
        if (Number.isFinite(gtrend[c]!)) predOk[c] = okY[c]!;
    }
  }
  if (!pred) return null;
  // an all-null surface is a failed recreation, not a map — fall back to the saved surface
  if (!pred.some((v) => Number.isFinite(v))) return null;

  const [lo, hi] = KRIGING_BOUNDS[prop] ?? [-Infinity, Infinity];
  const clamp = (arr: Float64Array) =>
    Array.from(arr, (v) =>
      Number.isFinite(v) ? Math.min(hi, Math.max(lo, v)) : null,
    );
  const values = clamp(pred);
  return {
    values,
    valuesOk: predOk ? clamp(predOk) : null,
    method,
    usedFallback,
  };
}

/*
 * The recreated surface as the left pane's layer — the same gridded shape as the IDW pane, on the
 * SAME colour scale as the saved map it recreates.
 */
export function recreatedLayer(
  values: (number | null)[],
  geoms: { type?: string; coordinates?: unknown }[],
  hr: ManifestLayer,
  prop: string,
): ManifestLayer {
  const features = geoms.reduce<unknown[]>((acc, g, i) => {
    const v = values[i];
    if (v != null && Number.isFinite(v))
      acc.push({
        type: "Feature",
        properties: { value: v, value_stored: v },
        geometry: g,
      });
    return acc;
  }, []);
  const present = values.filter(
    (v): v is number => v != null && Number.isFinite(v),
  );
  const vmin = present.length ? Math.min(...present) : 0;
  const vmax = present.length ? Math.max(...present) : 1;
  const label: Record<string, string> = {
    ph: "pH",
    om: "OM",
    cec: "CEC",
    p: "P",
    k: "K",
  };
  const layer: ManifestLayer = {
    id: `check-recreated-${prop}`,
    kind: "geojson",
    title: `${label[prop] ?? prop} (recreated high-res)`,
    inline: { type: "FeatureCollection", features } as never,
    colormap: "viridis",
    value_min: vmin,
    value_max: vmax > vmin ? vmax : vmin + 1,
    unit: hr.unit,
    default_visible: true,
    default_opacity: hr.default_opacity,
    order: 55,
    content_version: "check-accuracy",
    is_stale: false,
  } as ManifestLayer;
  return alignedIdwLayer(layer, hr);
}

// HOLDOUT VALIDATION — "pick 1/3 of the samples, evenly spatially distribute throughout the field
// and recalculate both the high-res map and the IDW map WITHOUT those samples … letting the user
// compare the results at the test samples").

/*
 * Below this a 1/3 holdout leaves too few training samples for the recreation (2n/3 must stay
 * ≥ MIN_KRIGE_POINTS).
 */
export const MIN_HOLDOUT_POINTS = 12;

export interface HoldoutSplit {
  test: IdwPoint[];
  train: IdwPoint[];
}

/*
 *
 * Deterministic farthest-point sampling: k = round(n/3) test points chosen to be EVENLY SPATIALLY
 * DISTRIBUTED. Seeded at the point farthest from the sample centroid, then greedily adding whichever
 * remaining point is farthest from every point already chosen (max–min). Deterministic on the same
 * input — reopening the view holds out the same third, so a comparison is repeatable, not a slot
 * machine. Metric distances, same equirect scaling as everything else here.
 */
/*
 *
 * the check-sample slider's bounds: far LEFT = the map keeps only this many.
 */
export const MIN_TRAIN_POINTS = 12;
/* * far RIGHT = only this many check samples */
export const MIN_TEST_POINTS = 4;

/*
 *
 *. n − MIN_TRAIN_POINTS] — far left of the slider trains on exactly 12, far right checks on
 * exactly 4; the default (and everything the slider cannot express, chosen = null) is the original
 * evenly-spread third, clamped into those bounds when they exist.
 */
export function holdoutCountFor(
  n: number,
  chosen: number | null | undefined,
): number {
  const third = Math.max(1, Math.round(n / 3));
  if (n <= MIN_TRAIN_POINTS + MIN_TEST_POINTS) return third;
  const lo = MIN_TEST_POINTS;
  const hi = n - MIN_TRAIN_POINTS;
  return Math.min(hi, Math.max(lo, Math.round(chosen ?? third)));
}

export function holdoutSplit(
  points: IdwPoint[],
  fraction = 1 / 3,
  count?: number | null,
): HoldoutSplit {
  const n = points.length;
  const k =
    count != null
      ? Math.min(Math.max(1, Math.round(count)), Math.max(1, n - 1))
      : Math.max(1, Math.round(n * fraction));
  if (n <= k) return { test: [...points], train: [] };
  const lat0 = points.reduce((a, p) => a + p.lat, 0) / n;
  const kx = METRES_PER_DEGREE_LNG * Math.cos((lat0 * Math.PI) / 180);
  const ky = METRES_PER_DEGREE_LAT;
  const xs = points.map((p) => p.lng * kx);
  const ys = points.map((p) => p.lat * ky);
  const cx = xs.reduce((a, v) => a + v, 0) / n;
  const cy = ys.reduce((a, v) => a + v, 0) / n;
  let seed = 0;
  let seedD = -1;
  for (let i = 0; i < n; i++) {
    const d = (xs[i]! - cx) ** 2 + (ys[i]! - cy) ** 2;
    if (d > seedD) {
      seedD = d;
      seed = i;
    }
  }
  const chosen = [seed];
  const minD = new Float64Array(n).fill(Infinity);
  for (let step = 1; step < k; step++) {
    const last = chosen[chosen.length - 1]!;
    let best = -1;
    let bestD = -1;
    for (let i = 0; i < n; i++) {
      if (chosen.includes(i)) continue;
      const d = (xs[i]! - xs[last]!) ** 2 + (ys[i]! - ys[last]!) ** 2;
      if (d < minD[i]!) minD[i] = d;
      if (minD[i]! > bestD) {
        bestD = minD[i]!;
        best = i;
      }
    }
    chosen.push(best);
  }
  const testSet = new Set(chosen);
  return {
    test: points.filter((_, i) => testSet.has(i)),
    train: points.filter((_, i) => !testSet.has(i)),
  };
}

/* * A surface's value at a point: its home (nearest-centroid) cell's value, null when unmapped. */
export function surfaceValueAt(
  pt: IdwPoint,
  centroids: { lng: number; lat: number }[],
  values: (number | null)[],
): number | null {
  const kx = METRES_PER_DEGREE_LNG * Math.cos((pt.lat * Math.PI) / 180);
  let best = -1;
  let bestD = Infinity;
  for (let c = 0; c < centroids.length; c++) {
    const d =
      ((centroids[c]!.lng - pt.lng) * kx) ** 2 +
      ((centroids[c]!.lat - pt.lat) * METRES_PER_DEGREE_LAT) ** 2;
    if (d < bestD) {
      bestD = d;
      best = c;
    }
  }
  return best >= 0 ? (values[best] ?? null) : null;
}

/*
 *
 * The sample overlay WITHOUT the held-out points — "those test samples should appear … as stars, not
 * dots", so their dot twins come off the layer. Matched by exact coordinates: both the layer features
 * and the IdwPoints carry the same `s.lon`/`s.lat` doubles, untouched, so equality is not fuzzy.
 */
export function withoutTestDots(
  layer: ManifestLayer,
  test: IdwPoint[],
): ManifestLayer {
  const keys = new Set(test.map((p) => `${p.lng},${p.lat}`));
  const fc = layer.inline as unknown as GeoJSON.FeatureCollection | undefined;
  if (!fc?.features) return layer;
  const features = fc.features.filter((f) => {
    if (f.geometry?.type !== "Point") return true;
    const [lng, lat] = f.geometry.coordinates as [number, number];
    return !keys.has(`${lng},${lat}`);
  });
  return { ...layer, inline: { ...fc, features } as never };
}

/*
 *
 * "About the same" significance thresholds, per property, in the STORED M3-8" basis the errors are
 * computed in: a sample where the two maps' distances to truth differ by LESS than this is neither
 * map's win. 5 ppm P, 10 ppm K, 0.2 %OM, 0.2 pH, 3 meq/100g CEC.
 */
export const SIGNIFICANCE_DELTA: Record<string, number> = {
  p: 5,
  k: 10,
  om: 0.2,
  ph: 0.2,
  cec: 3,
};
/*
 *
 * "% of the field that the AI Map improves": net wins over the whole test (significantly better −
 * significantly worse) / compared, as a percent.
 */
export function aiImprovedPct(
  stats: Pick<HoldoutStats, "sigBetter" | "sigWorse" | "compared">,
): number | null {
  if (!stats.compared) return null;
  return ((stats.sigBetter - stats.sigWorse) / stats.compared) * 100;
}

// 16,000 = the VR seeding swing: prescriptions typically move population ~16,000 seeds/ac across a
// 1% OM difference (OM ⇒ water-holding ⇒ carrying capacity).

export interface AgronomicValueInput {
  /* * stored-basis median map gap at the significant samples (null = none were significant) */
  gap: number | null;
  /* * aiImprovedPct — the PERCENT form, e.g. 17 for +17% */
  pct: number | null;
}

export interface AgronomicValue {
  dapLbs: number | null;
  potashLbs: number | null;
  limeLbs: number | null;
  seedPop: number | null;
}

export function agronomicValueCreated(
  by: Partial<
    Record<"p" | "k" | "ph" | "cec" | "om", AgronomicValueInput | null>
  >,
  fieldAcres: number,
): AgronomicValue {
  const frac = (x: AgronomicValueInput) => (x.pct ?? 0) / 100;
  const gap = (x: AgronomicValueInput) => x.gap ?? 0;
  const pIn = by.p ?? null;
  const kIn = by.k ?? null;
  const phIn = by.ph ?? null;
  const cecIn = by.cec ?? null;
  const omIn = by.om ?? null;
  return {
    dapLbs: pIn ? gap(pIn) * frac(pIn) * fieldAcres * 12 : null,
    potashLbs: kIn ? gap(kIn) * frac(kIn) * fieldAcres * 4.8 : null,
    // lime needs BOTH the pH and CEC holdouts — its formula reads all four of their numbers
    limeLbs:
      phIn && cecIn
        ? Math.max(gap(phIn), 0.2) *
          ((frac(phIn) + frac(cecIn)) / 2) *
          Math.max(gap(cecIn), 4) *
          fieldAcres *
          640
        : null,
    seedPop: omIn ? gap(omIn) * frac(omIn) * fieldAcres * 16_000 : null,
  };
}

// A null quantity keeps a null price line; the TOTAL sums whatever lines exist and is null only
// when ALL of them are (a partial total is still information).
export const AGRO_PRICES = {
  dapPerLb: 0.46,
  potashPerLb: 0.25,
  limePerLb: 20 / 2000,
  seedPerKernel: 270 / 80_000,
} as const;

export interface EconomicValue {
  dapUsd: number | null;
  potashUsd: number | null;
  limeUsd: number | null;
  seedUsd: number | null;
  totalUsd: number | null;
}

export function economicValueCreated(v: AgronomicValue): EconomicValue {
  const dapUsd = v.dapLbs != null ? v.dapLbs * AGRO_PRICES.dapPerLb : null;
  const potashUsd =
    v.potashLbs != null ? v.potashLbs * AGRO_PRICES.potashPerLb : null;
  const limeUsd = v.limeLbs != null ? v.limeLbs * AGRO_PRICES.limePerLb : null;
  const seedUsd =
    v.seedPop != null ? v.seedPop * AGRO_PRICES.seedPerKernel : null;
  const parts = [dapUsd, potashUsd, limeUsd, seedUsd].filter(
    (x): x is number => x != null,
  );
  return {
    dapUsd,
    potashUsd,
    limeUsd,
    seedUsd,
    totalUsd: parts.length ? parts.reduce((a, b) => a + b, 0) : null,
  };
}

// " The premise is the suite's own: the AI map is the better truth estimate (that is what the
// holdout demonstrates), and the kriged map is what a grower would act on without it. So a cell's
// mistake is what acting on the KRIGED reading would do there, judged against the AI reading P / K:
// the Rx falls as the soil test rises — a kriged reading HIGH of truth under-spreads ("not
// enough"), a reading LOW of truth over-spreads ("too much").
export type SpreadMistake = "under" | "over" | "ok";

/*
 * per product: the verb for the toggle + the three region labels. NO cec entry — no product rides
 * CEC directly (it enters the lime formula as a factor; pH is the driver), so cec offers no overlay
 */
export const MISTAKE_WORDS: Record<
  string,
  { product: string; verb: string; under: string; over: string; ok: string }
> = {
  p: {
    product: "DAP",
    verb: "Spreading",
    under: "Not spreading enough DAP",
    over: "Spreading too much DAP",
    ok: "Spreading DAP correctly",
  },
  k: {
    product: "Potash",
    verb: "Spreading",
    under: "Not spreading enough potash",
    over: "Spreading too much potash",
    ok: "Spreading potash correctly",
  },
  ph: {
    product: "Lime",
    verb: "Spreading",
    under: "Not spreading enough lime",
    over: "Spreading too much lime",
    ok: "Spreading lime correctly",
  },
  om: {
    product: "Seed",
    verb: "Planting",
    under: "Planting too thin",
    over: "Planting too thick",
    ok: "Planting correctly",
  },
};

/*
 * under = the crop starves there (red); over = money wasted (violet — NOT the verdict-ring red/green
 * pair's amber star); ok = green. Fill colours for the overlay and its legend chips.
 */
export const MISTAKE_COLORS: Record<SpreadMistake, string> = {
  under: "#dc2626",
  over: "#7c3aed",
  ok: "#16a34a",
};

/*
 * Classify every cell: what acting on the kriged reading would do there, judged against the AI
 * reading. null where either surface has no answer.
 */
export function spreadMistakes(
  aiVals: (number | null)[],
  krigeVals: (number | null)[],
  prop: string,
): (SpreadMistake | null)[] {
  const delta = SIGNIFICANCE_DELTA[prop];
  const words = MISTAKE_WORDS[prop];
  return aiVals.map((ai, i) => {
    const kr = krigeVals[i];
    if (
      words == null ||
      delta == null ||
      ai == null ||
      kr == null ||
      !Number.isFinite(ai) ||
      !Number.isFinite(kr)
    )
      return null;
    // p/k/ph: reads-high ⇒ under-applies; om inverts (seed Rx rises with OM)
    const d = (kr - ai) * (prop === "om" ? -1 : 1);
    return d > delta ? "under" : d < -delta ? "over" : "ok";
  });
}

/*
 * The overlay FeatureCollection: one feature per classified cell, carrying its class and fill colour.
 * Contiguous same-class cells read as the three regions; unclassifiable cells are simply absent.
 */
export function mistakeOverlayFc(
  geoms: { type?: string; coordinates?: unknown }[],
  classes: (SpreadMistake | null)[],
): GeoJSON.FeatureCollection {
  const features: GeoJSON.Feature[] = [];
  geoms.forEach((g, i) => {
    const c = classes[i];
    if (c == null || g?.type == null || g.coordinates == null) return;
    features.push({
      type: "Feature",
      geometry: g as GeoJSON.Geometry,
      properties: { mistake: c, color: MISTAKE_COLORS[c] },
    });
  });
  return { type: "FeatureCollection", features };
}

/*
 *
 * Returns null when the property can't be scored (too few samples, no recreation) — the caller
 * treats null as "not a win".
 */
export async function runHoldoutScore(
  fieldId: string,
  target: CheckAccuracyTarget,
): Promise<HoldoutStats | null> {
  if (target.points.length < MIN_HOLDOUT_POINTS) return null;
  const { stackCellData } = await import("./stackClient");
  const cell = await stackCellData(fieldId, [
    UNCORRECTED_KEY[target.prop] ?? "",
  ]);
  // the PANEL's default count (clamped past 16 samples) — an unclamped third diverges at n=17
  const { test, train } = holdoutSplit(
    target.points,
    1 / 3,
    holdoutCountFor(target.points.length, null),
  );
  if (train.length < MIN_KRIGE_POINTS) return null;
  const gen = recreateHighRes(
    train,
    cell.centroids,
    cell.features[0] ?? [],
    target.prop,
  );
  let step = gen.next();
  while (!step.done) {
    await new Promise((r) => setTimeout(r, 0)); // hand the loop back between EBK bags
    step = gen.next();
  }
  if (!step.value) return null;
  const krige = sampleKrigeSurface(train, cell.centroids);
  const ek = test.map((pt) =>
    surfaceValueAt(pt, cell.centroids, step.value!.values),
  );
  const ok = step.value.valuesOk
    ? test.map((pt) =>
        surfaceValueAt(pt, cell.centroids, step.value!.valuesOk!),
      )
    : null;
  const kr = test.map((pt) => surfaceValueAt(pt, cell.centroids, krige.values));
  const tuned = autoTuneMu(test, ok, ek, kr, target.prop);
  const scored = ok && tuned < 1 ? lerpSurfaces(ok, ek, tuned) : ek;
  return holdoutStats(test, scored, kr, target.prop);
}

/* * the deltas' (and medianSigDiff's) units + display precision, stored M3-8″ basis */
export const PROP_UNIT: Record<string, string> = {
  p: "ppm",
  k: "ppm",
  om: "% OM",
  ph: "pH",
  cec: "meq/100g",
};
export const PROP_DECIMALS: Record<string, number> = {
  p: 1,
  k: 1,
  om: 2,
  ph: 2,
  cec: 1,
};

export interface HoldoutStats {
  /* * samples where the AI map's error beat the kriged map's by at least the significance delta */
  sigBetter: number;
  /* * samples where the two errors differ by less than the delta — neither map's win */
  same: number;
  /* * samples where the AI map's error was worse by at least the delta */
  sigWorse: number;
  /* * test samples where BOTH surfaces had a prediction */
  compared: number;
  /*
   *
   * median |high-res − kriged| over the SIGNIFICANTLY-DIFFERENT samples only “the median difference
   * between the high-res map and the kriged map ON THE SAMPLES THAT WERE SIGNIFICANTLY DIFFERENT”)
   * — how far apart the maps actually are where one meaningfully beat the other; null when no
   * sample was significantly different.
   */
  medianSigDiff?: number | null;
  winner: "ai" | "krige" | "tie";
}

/*
 *
 * Score the holdout: per test sample, each surface's |prediction − truth|, bucketed by the property's
 * significance delta. Samples where either surface has no prediction (an unmapped cell) are excluded
 * entirely, so every count describes the same set.
 */
export type HoldoutVerdict = "better" | "same" | "worse";

/*
 *
 * Per-test-sample verdict under the property's significance delta — THE one bucketing added the map
 * rings, so the buckets now paint pixels as well as counts; `holdoutStats` consumes this so the
 * panel's numbers and the rings on the stars cannot disagree).
 */
export function holdoutVerdicts(
  test: IdwPoint[],
  aiVals: (number | null)[],
  krigeVals: (number | null)[],
  prop: string,
): (HoldoutVerdict | null)[] {
  const delta = SIGNIFICANCE_DELTA[prop] ?? 0;
  return test.map((pt, i) => {
    const a = aiVals[i];
    const k = krigeVals[i];
    if (a == null || k == null || !Number.isFinite(a) || !Number.isFinite(k))
      return null;
    const gap = Math.abs(k - pt.value) - Math.abs(a - pt.value); // >0 = AI closer to truth
    return Math.abs(gap) < delta ? "same" : gap > 0 ? "better" : "worse";
  });
}

export function holdoutStats(
  test: IdwPoint[],
  aiVals: (number | null)[],
  krigeVals: (number | null)[],
  prop: string,
): HoldoutStats | null {
  const verdicts = holdoutVerdicts(test, aiVals, krigeVals, prop);
  let sigBetter = 0;
  let same = 0;
  let sigWorse = 0;
  let compared = 0;
  const sigDiffs: number[] = [];
  for (let i = 0; i < verdicts.length; i++) {
    const v = verdicts[i];
    if (v == null) continue;
    compared++;
    if (v === "same") same++;
    else {
      if (v === "better") sigBetter++;
      else sigWorse++;
      sigDiffs.push(Math.abs(aiVals[i]! - krigeVals[i]!));
    }
  }
  if (!compared) return null;
  sigDiffs.sort((a, b) => a - b);
  const medianSigDiff = sigDiffs.length
    ? sigDiffs.length % 2
      ? sigDiffs[(sigDiffs.length - 1) / 2]!
      : (sigDiffs[sigDiffs.length / 2 - 1]! + sigDiffs[sigDiffs.length / 2]!) /
        2
    : null;
  return {
    sigBetter,
    same,
    sigWorse,
    compared,
    medianSigDiff,
    winner:
      sigBetter > sigWorse ? "ai" : sigWorse > sigBetter ? "krige" : "tie",
  };
}

export { AI_WIN_PCT } from "./aiWin";

/*
 *
 * If it never wins: the HIGHEST μ that was a TIE. If it never tied either: 0%.
 */
export function autoTuneMu(
  test: IdwPoint[],
  hrOkVals: (number | null)[] | null,
  hrEkVals: (number | null)[],
  krigeVals: (number | null)[],
  prop: string,
): number {
  if (!hrOkVals) return 1;
  let highestTie: number | null = null;
  for (let step = 20; step >= 0; step--) {
    const mu = step / 20;
    const scored = mu === 1 ? hrEkVals : lerpSurfaces(hrOkVals, hrEkVals, mu);
    const stats = holdoutStats(test, scored, krigeVals, prop);
    if (!stats) return 1;
    if (stats.winner === "ai") return mu;
    if (stats.winner === "tie" && highestTie == null) highestTie = mu;
  }
  return highestTie ?? 0;
}
